import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AudioService from '../services/audioService';
import './VoiceRecorder.css';

const VoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [currentRecording, setCurrentRecording] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [recordingTitle, setRecordingTitle] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [defaultChannel, setDefaultChannel] = useState('');
  const [channelWarning, setChannelWarning] = useState('');
  const [topicType, setTopicType] = useState('meeting');
  const [expandedRecording, setExpandedRecording] = useState(null);
  const [transcriptionContent, setTranscriptionContent] = useState({});
  const [summaryContent, setSummaryContent] = useState({});

  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const dbRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    initIndexedDB();
    loadRecordings();
    loadServerRecordings();
    loadChannels();
    
    if (location.state?.autoStart) {
      setRecordingTitle(location.state.title || '');
      setSelectedChannel(location.state.selectedChannel || '');
      setTimeout(() => {
        if (location.state.title && location.state.selectedChannel) {
          startRecording();
        }
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      setUploadProgress({});
      setError(null);
    };
  }, [location.state]);

  const initIndexedDB = async () => {
    try {
      const request = indexedDB.open('VoiceRecordings', 1);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('recordings')) {
          const store = db.createObjectStore('recordings', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        dbRef.current = event.target.result;
      };

      request.onerror = (event) => {
        console.error('IndexedDB 초기화 실패:', event.target.error);
      };
    } catch (error) {
      console.error('IndexedDB 설정 오류:', error);
    }
  };

  const loadRecordings = async () => {
    if (!dbRef.current) return;

    try {
      const transaction = dbRef.current.transaction(['recordings'], 'readonly');
      const store = transaction.objectStore('recordings');
      const request = store.getAll();

      request.onsuccess = () => {
        const savedRecordings = request.result;
        setRecordings(prev => {
          const serverRecordings = prev.filter(r => r.isServerRecording);
          return [...savedRecordings, ...serverRecordings].sort((a, b) => 
            new Date(b.timestamp) - new Date(a.timestamp)
          );
        });
      };
    } catch (error) {
      console.error('녹음 목록 로드 실패:', error);
    }
  };

  const loadServerRecordings = async () => {
    try {
      const audioService = new AudioService();
      const result = await audioService.getAudioList(1, 100);
      
      if (result.success && result.items) {
        const serverRecordings = result.items.map((item, index) => ({
          id: `server_${item.id || item.audio_id || Date.now() + index}`,
          serverId: item.id || item.audio_id,
          timestamp: new Date(item.upload_timestamp || item.created_at),
          duration: item.duration || 0,
          size: item.size || 0,
          filename: item.filename,
          title: item.title || `녹음 ${new Date(item.upload_timestamp || item.created_at).toLocaleString()}`,
          isServerRecording: true,
          stt_origin_s3_uri: item.stt_origin_s3_uri,
          stt_refined_s3_uri: item.stt_refined_s3_uri
        }));
        
        setRecordings(prev => {
          const localRecordings = prev.filter(r => !r.isServerRecording);
          return [...localRecordings, ...serverRecordings].sort((a, b) => 
            new Date(b.timestamp) - new Date(a.timestamp)
          );
        });
      }
    } catch (error) {
      console.error('서버 녹음 목록 로드 실패:', error);
    }
  };

  const loadChannels = async () => {
    try {
      const { fetchChannels, fetchUserProfile } = await import('../services/api');
      
      // 채널 목록 로드
      const channelsResult = await fetchChannels();
      if (channelsResult.success) {
        setChannels(channelsResult.channels);
      } else {
        console.error('채널 목록 로드 실패:', channelsResult.error);
      }
      
      // 사용자 프로필 로드 (기본 채널 정보)
      try {
        const profileResult = await fetchUserProfile();
        if (profileResult.success && profileResult.profile.default_channel) {
          const defaultCh = profileResult.profile.default_channel;
          setDefaultChannel(defaultCh);
          setSelectedChannel(defaultCh);
        }
      } catch (error) {
        console.log('사용자 프로필 로드 실패 (선택사항):', error);
      }
    } catch (error) {
      console.error('채널 목록 로드 오류:', error);
    }
  };

  const handleChannelChange = (channelValue) => {
    setSelectedChannel(channelValue);
    
    if (channelValue) {
      const selectedChannelData = channels.find(ch => 
        `${ch.channel_type}:${ch.channel_id}` === channelValue
      );
      
      if (selectedChannelData && !selectedChannelData.project) {
        setChannelWarning('해당 채널에는 프로젝트가 설정되어 있지 않습니다. 이경우 KB 활용 및 고품질의 STT 결과를 얻을수 없습니다.');
      } else {
        setChannelWarning('');
      }
    } else {
      setChannelWarning('');
    }
  };

  const getSelectedChannelProject = () => {
    if (!selectedChannel) return null;
    const selectedChannelData = channels.find(ch => 
      `${ch.channel_type}:${ch.channel_id}` === selectedChannel
    );
    return selectedChannelData?.project || null;
  };

  const handleStartRecording = () => {
    setShowTitleModal(true);
    setRecordingTitle('');
    setSelectedChannel(defaultChannel);
    setTopicType('meeting');
  };

  const handleFileUpload = () => {
    setShowUploadModal(true);
    setRecordingTitle('');
    setUploadFile(null);
    setSelectedChannel(defaultChannel);
    setTopicType('meeting');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    const allowedTypes = ['audio/', 'video/mp4'];
    const allowedExtensions = ['.mp4', '.m4a', '.wav', '.mp3', '.aac'];
    
    if (file && (allowedTypes.some(type => file.type.startsWith(type)) || 
                 allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext)))) {
      setUploadFile(file);
    } else {
      setError('오디오 파일만 업로드 가능합니다. (지원 형식: MP4, M4A, WAV, MP3, AAC)');
    }
  };

  const uploadAudioFile = async () => {
    if (!uploadFile || !selectedChannel) {
      setError('파일과 알림 채널을 선택해주세요');
      return;
    }

    try {
      setError(null);
      setShowUploadModal(false);

      const audioBlob = new Blob([uploadFile], { type: uploadFile.type });
      
      const recording = {
        id: Date.now(),
        blob: audioBlob,
        timestamp: new Date(),
        duration: 0,
        size: uploadFile.size,
        title: recordingTitle.trim() || `업로드 ${new Date().toLocaleString()}`,
        filename: uploadFile.name
      };

      if (dbRef.current) {
        const transaction = dbRef.current.transaction(['recordings'], 'readwrite');
        const store = transaction.objectStore('recordings');
        await store.add(recording);
      }

      setRecordings(prev => [recording, ...prev].sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      ));
      setCurrentRecording(recording);
      uploadToServer(recording);

    } catch (error) {
      console.error('파일 업로드 실패:', error);
      setError('파일 업로드에 실패했습니다.');
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      setShowTitleModal(false);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 44100,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      audioStreamRef.current = stream;
      chunksRef.current = [];

      const options = {
        mimeType: 'audio/mp4',
        audioBitsPerSecond: 128000
      };

      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/mp4' });
        await saveRecording(audioBlob);
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      startTimer();

    } catch (error) {
      console.error('녹음 시작 실패:', error);
      setError('마이크 접근 권한이 필요합니다. 브라우저 설정을 확인해주세요.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      stopTimer();
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        startTimer();
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        stopTimer();
      }
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecordingTime(0);
  };

  const saveRecording = async (audioBlob) => {
    const recording = {
      id: Date.now(),
      blob: audioBlob,
      timestamp: new Date(),
      duration: recordingTime,
      size: audioBlob.size,
      title: recordingTitle || `녹음 ${new Date().toLocaleString()}`
    };

    try {
      if (dbRef.current) {
        const transaction = dbRef.current.transaction(['recordings'], 'readwrite');
        const store = transaction.objectStore('recordings');
        await store.add(recording);
      }

      setRecordings(prev => [recording, ...prev].sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      ));
      setCurrentRecording(recording);
      uploadToServer(recording);

    } catch (error) {
      console.error('녹음 저장 실패:', error);
      setError('녹음 저장에 실패했습니다.');
    }
  };

  const uploadToServer = async (recording) => {
    try {
      setUploadProgress(prev => ({ ...prev, [recording.id]: 0 }));
      
      const audioService = new AudioService();
      const metadata = {
        duration: recording.duration,
        timestamp: recording.timestamp.toISOString(),
        localId: recording.id,
        title: recording.title || '',
        channel: selectedChannel || '',
        topic_type: topicType || 'meeting',
        mimeType: recording.blob.type || 'audio/mp4',
        size: recording.size || recording.blob.size,
        filename: recording.filename || ''
      };

      const onProgress = (progress) => {
        setUploadProgress(prev => ({ ...prev, [recording.id]: progress.percentage }));
      };

      const result = await audioService.uploadAudioChunked(
        recording.blob, 
        metadata,
        onProgress
      );
      
      setUploadProgress(prev => {
        const newStatus = { ...prev };
        delete newStatus[recording.id];
        return newStatus;
      });
      
      // 업로드 완료 후 isUploaded 플래그 설정
      setRecordings(prev => prev.map(r => 
        r.id === recording.id ? { ...r, isUploaded: true } : r
      ));
      
      updateRecordingStatus(recording.id, { 
        serverId: result.id 
      });

    } catch (error) {
      console.error('서버 업로드 실패:', error);
      setUploadProgress(prev => {
        const newStatus = { ...prev };
        delete newStatus[recording.id];
        return newStatus;
      });
      setError('업로드에 실패했습니다.');
    }
  };

  const updateRecordingStatus = async (id, updates) => {
    if (!dbRef.current) return;

    try {
      const transaction = dbRef.current.transaction(['recordings'], 'readwrite');
      const store = transaction.objectStore('recordings');
      const request = store.get(id);

      request.onsuccess = () => {
        const recording = request.result;
        if (recording) {
          Object.assign(recording, updates);
          store.put(recording);
        }
      };
    } catch (error) {
      console.error('녹음 상태 업데이트 실패:', error);
    }
  };

  const downloadRecording = async (recording) => {
    if (recording.isServerRecording) {
      try {
        const audioService = new AudioService();
        const downloadUrl = await audioService.getDownloadUrl(recording.serverId);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = recording.filename || `recording_${recording.serverId}.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        console.error('서버 녹음 다운로드 실패:', error);
        setError('녹음 다운로드에 실패했습니다.');
      }
    } else {
      const url = URL.createObjectURL(recording.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording_${recording.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const deleteRecording = async (id) => {
    try {
      const recording = recordings.find(r => r.id === id);
      
      setRecordings(prev => prev.filter(r => r.id !== id));
      setUploadProgress(prev => {
        const newStatus = { ...prev };
        delete newStatus[id];
        if (recording?.serverId) {
          delete newStatus[recording.serverId];
        }
        return newStatus;
      });
      
      if (recording?.isServerRecording) {
        const audioService = new AudioService();
        await audioService.deleteAudio(recording.serverId);
      } else {
        if (dbRef.current) {
          const transaction = dbRef.current.transaction(['recordings'], 'readwrite');
          const store = transaction.objectStore('recordings');
          await store.delete(id);
        }
      }

    } catch (error) {
      console.error('녹음 삭제 실패:', error);
      setError('녹음 삭제에 실패했습니다.');
      loadRecordings();
      loadServerRecordings();
    }
  };



  const viewTranscription = async (recording) => {
    try {
      if (expandedRecording === recording.id && transcriptionContent[recording.id]) {
        setExpandedRecording(null);
        return;
      }
      
      const audioService = new AudioService();
      const result = await audioService.getTranscription(recording.serverId || recording.id);
      
      const content = result.transcription || '전사 결과가 없습니다.';
      setTranscriptionContent(prev => ({ ...prev, [recording.id]: content }));
      setExpandedRecording(recording.id);
    } catch (error) {
      console.error('전사 파일 조회 실패:', error);
      setError('전사 파일을 찾을 수 없습니다.');
    }
  };

  const viewSummary = async (recording) => {
    try {
      if (expandedRecording === recording.id && summaryContent[recording.id]) {
        setExpandedRecording(null);
        return;
      }
      
      const audioService = new AudioService();
      const result = await audioService.getSummary(recording.serverId || recording.id);
      
      const content = result.summary || '요약 문서가 없습니다.';
      setSummaryContent(prev => ({ ...prev, [recording.id]: content }));
      setExpandedRecording(recording.id);
    } catch (error) {
      console.error('요약 문서 조회 실패:', error);
      setError('요약 문서를 찾을 수 없습니다.');
    }
  };

  const openContentWindow = (title, content, type) => {
    const newWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
          }
          h1, h2, h3 { color: #2196f3; }
          pre { background: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; }
          .text-content { white-space: pre-wrap; word-wrap: break-word; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        ${type === 'markdown' ? 
          `<div>${content.replace(/\n/g, '<br>')}</div>` : 
          `<div class="text-content">${content}</div>`
        }
      </body>
      </html>
    `;
    
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUploadProgress = (recording) => {
    return uploadProgress[recording.id] || null;
  };

  return (
    <div className="voice-recorder">
      <div className="recorder-header">
        <h2>음성 녹음</h2>
        <div className="recorder-info">
          <span>Safari 최적화 • 로컬 + 서버 저장</span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="recording-controls">
        <div className="main-controls">
          {!isRecording ? (
            <div className="control-buttons">
              <button 
                className="btn btn-record"
                onClick={handleStartRecording}
              >
                🎤 녹음 시작
              </button>
              <button 
                className="btn btn-upload"
                onClick={handleFileUpload}
              >
                📁 파일 업로드
              </button>
            </div>
          ) : (
            <div className="recording-actions">
              <button 
                className="btn btn-stop"
                onClick={stopRecording}
              >
                ⏹️ 중지
              </button>
            </div>
          )}
        </div>

        {isRecording && (
          <div className="recording-status">
            <div className="recording-indicator">
              <div className="pulse-dot"></div>
              <span>녹음 중 {isPaused ? '(일시정지)' : ''}</span>
            </div>
            <div className="recording-time">
              {formatTime(recordingTime)}
            </div>
          </div>
        )}
      </div>

      <div className="recordings-list">
        <div className="list-header">
          <h3>녹음 목록</h3>
          <span className="recordings-count">{recordings.length}개</span>
        </div>

        {recordings.length === 0 ? (
          <div className="empty-state">
            아직 녹음된 파일이 없습니다.
          </div>
        ) : (
          <div className="recordings-grid">
            {recordings.map((recording) => (
              <div key={recording.id} className="recording-item">
                <div className="recording-info">
                  <div className="recording-title">
                    {recording.title}
                  </div>
                  <div className="recording-id">
                    {recording.serverId && recording.serverId !== 'undefined' ? recording.serverId : recording.id}
                  </div>
                  <div className="recording-date">
                    {new Date(recording.timestamp).toLocaleString()}
                  </div>
                  <div className="recording-meta">
                    {getUploadProgress(recording) !== null && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${getUploadProgress(recording)}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">업로드중... {getUploadProgress(recording)}%</span>
                      </div>
                    )}
                    {recording.isUploaded && <span>📁 업로드됨</span>}
                  </div>
                </div>
                
                <div className="recording-actions">
                  {recording.stt_origin_s3_uri && (
                    <button 
                      className="btn-icon"
                      onClick={() => viewTranscription(recording)}
                      title="전사 파일 보기"
                    >
                      📝
                    </button>
                  )}
                  {recording.stt_refined_s3_uri && (
                    <button 
                      className="btn-icon"
                      onClick={() => viewSummary(recording)}
                      title="요약 문서 보기"
                    >
                      📄
                    </button>
                  )}
                  <button 
                    className="btn-icon btn-danger"
                    onClick={() => deleteRecording(recording.id)}
                    title="삭제"
                  >
                    🗑️
                  </button>
                </div>
                
                {expandedRecording === recording.id && (
                  <div className="recording-content">
                    {transcriptionContent[recording.id] && (
                      <div className="content-section">
                        <h4>전사 결과</h4>
                        <div className="content-text">{transcriptionContent[recording.id]}</div>
                      </div>
                    )}
                    {summaryContent[recording.id] && (
                      <div className="content-section">
                        <h4>요약 문서</h4>
                        <div className="content-text" dangerouslySetInnerHTML={{ 
                          __html: summaryContent[recording.id].replace(/\n/g, '<br>') 
                        }}></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showTitleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>녹음 제목 입력</h3>
            
            <div className="form-group">
              <label>타입</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  className={`btn ${topicType === 'meeting' ? 'btn-type-selected' : 'btn-type'}`}
                  onClick={() => setTopicType('meeting')}
                  style={{ flex: 1 }}
                >
                  회의
                </button>
                <button 
                  className="btn btn-type"
                  onClick={() => alert('준비중입니다. 요약 프롬프트를 지정할수 있습니다.')}
                  style={{ flex: 1 }}
                >
                  +커스텀
                </button>
              </div>
            </div>
            
            <input
              type="text"
              value={recordingTitle}
              onChange={(e) => setRecordingTitle(e.target.value)}
              placeholder="(선택) 녹음 제목을 입력하세요"
              className="title-input"
              autoFocus
            />
            
            <div className="form-group">
              <label>알림 채널 (맵핑된 프로젝트에서 컨텍스트를 참조합니다)</label>
              <select
                value={selectedChannel}
                onChange={(e) => handleChannelChange(e.target.value)}
                className="channel-select"
              >
                <option value="">채널을 선택하세요</option>
                {channels.map((channel) => (
                  <option key={channel.pk} value={`${channel.channel_type}:${channel.channel_id}`}>
                    {channel.channel_name}
                  </option>
                ))}
              </select>
            </div>
            
            {getSelectedChannelProject() && (
              <div className="project-info">
                연결된 프로젝트: {getSelectedChannelProject()}
              </div>
            )}
            
            {channelWarning && (
              <div className="channel-warning">
                {channelWarning}
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => setShowTitleModal(false)}
              >
                취소
              </button>
              <button 
                className="btn btn-confirm"
                onClick={startRecording}
                disabled={!selectedChannel}
              >
                녹음 시작
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>음성 파일 업로드</h3>
            
            <div className="form-group">
              <label>타입</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  className={`btn ${topicType === 'meeting' ? 'btn-type-selected' : 'btn-type'}`}
                  onClick={() => setTopicType('meeting')}
                  style={{ flex: 1 }}
                >
                  회의
                </button>
                <button 
                  className="btn btn-type"
                  onClick={() => alert('준비중입니다. 요약 프롬프트를 지정할수 있습니다.')}
                  style={{ flex: 1 }}
                >
                  +커스텀
                </button>
              </div>
            </div>
            
            <input
              type="file"
              accept="audio/*,.mp4,.m4a,.wav,.mp3,.aac"
              onChange={handleFileSelect}
              className="file-input"
            />
            {uploadFile && (
              <div className="file-info">
                <p>선택된 파일: {uploadFile.name}</p>
                <p>크기: {formatFileSize(uploadFile.size)}</p>
              </div>
            )}
            <input
              type="text"
              value={recordingTitle}
              onChange={(e) => setRecordingTitle(e.target.value)}
              placeholder="(선택) 파일 제목을 입력하세요"
              className="title-input"
            />
            
            <div className="form-group">
              <label>알림 채널 (맵핑된 프로젝트에서 컨텍스트를 참조합니다)</label>
              <select
                value={selectedChannel}
                onChange={(e) => handleChannelChange(e.target.value)}
                className="channel-select"
              >
                <option value="">채널을 선택하세요</option>
                {channels.map((channel) => (
                  <option key={channel.pk} value={`${channel.channel_type}:${channel.channel_id}`}>
                    {channel.channel_name}
                  </option>
                ))}
              </select>
            </div>
            
            {getSelectedChannelProject() && (
              <div className="project-info">
                연결된 프로젝트: {getSelectedChannelProject()}
              </div>
            )}
            
            {channelWarning && (
              <div className="channel-warning">
                {channelWarning}
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => setShowUploadModal(false)}
              >
                취소
              </button>
              <button 
                className="btn btn-confirm"
                onClick={uploadAudioFile}
                disabled={!uploadFile || !selectedChannel}
              >
                업로드
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;